import { ng1_directive } from "@uirouter/angularjs/lib/directives/stateDirectives";
import * as Common from "../Common/modules";
import * as Models from "../Models";
import { IEpcOverrideReconcileRequest } from "../Models/CostCalculation/EpcOverrideReconcileRequest";

export interface ICostCalculationApiClient {
    calculateEpcNoLog(costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IEpcCalculationResult>;
    calculateEpcWithLog(costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IEpcCalculationResult>;
    calculateEpcAndAssociateOpportunityEpcCalculation(costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IOpportunityAssociatedEpcCalculationResult>;
    calculateEpcAndReconcileContractEpcCalculation(costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult>;

    getActiveOpportunityAssociatedEpcCalculations(partnerSystemNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IOpportunityAssociatedEpcCalculationResult[]>;
    removeOpportunityAssociatedEpcCalculation(removeEpcRequest: Models.CostCalculation.RemoveEpcCalculationRequest.IRemoveEpcCalculationRequest): ng.IPromise<void>;
    removeOpportunityAssociatedEpcCalculationAndVoidEnvelopes(removeEpcRequest: Models.CostCalculation.RemoveEpcCalculationRequest.IRemoveEpcCalculationRequest): ng.IPromise<void>;
    getContractEpcCalculation(igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult>;
    getPendingEpcCalculation(igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult>;
    updateUnshadedPercent(request: Models.CostCalculation.UpdateUnshadedPercentRequest.UpdateUnshadedPercentRequest): ng.IPromise<void>;

    // Should this be in an AccountApiClient/AccountDetailsService?
    getCustomerContractSignDate(customerDetailsRequest: Models.Customer.SolarCustomerDetailsRequest.SolarCustomerDetailsRequest): ng.IPromise<Date>;
    isContractAmendmentRateInProgress(igsCustomerNumber: string): ng.IPromise<boolean>;
    cancelContractAmendment(igsCustomerNumber: string): ng.IPromise<void>;
    getProductionEstimateReconcileSummary(igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.ProductionEstimateResult.ProductionEstimateReconcileSummary>;
    calculateEpcAndReconcileWithProductionEstimate(igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult>;
    confirmReconciledSystemData(igsCustomerNumber: string): ng.IPromise<void>;
    reconcileEpcCostWithOverride(overrideRequest: IEpcOverrideReconcileRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult>;
}

export class CostCalculationApiClient implements ICostCalculationApiClient {
    public static $inject = ["igs.apiClientService"];
    public prefix: string;

    constructor(private igsApiClientService: Common.ApiClientService.Igs.Common.IApiClientService) {
        this.prefix = "CostCalculation";
    }

    public calculateEpcNoLog = (costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IEpcCalculationResult> => {
        const url = `${this.prefix}/CalculateEpc?log=false&test=false`;
        const config = { nomask: true };
        return this.igsApiClientService.post(url, costCalculationModel, config);
    }

    public calculateEpcWithLog = (costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IEpcCalculationResult> => {
        const url = `${this.prefix}/CalculateEpc?log=true&test=false`;
        return this.igsApiClientService.post(url, costCalculationModel);
    }

    public calculateEpcAndAssociateOpportunityEpcCalculation = (costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IOpportunityAssociatedEpcCalculationResult> => {
        const url = `${this.prefix}/CalculateEpcAndAssociateOpportunityEpcCalculation`;
        return this.igsApiClientService.post(url, costCalculationModel);
    }

    public calculateEpcAndReconcileContractEpcCalculation = (costCalculationModel: Models.CostCalculation.EpcRequest.IEpcCalculationRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult> => {
        const url = `${this.prefix}/CalculateEpcAndReconcileContractEpcCalculation`;
        return this.igsApiClientService.post(url, costCalculationModel);
    }

    public getActiveOpportunityAssociatedEpcCalculations = (partnerSystemNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IOpportunityAssociatedEpcCalculationResult[]> => {
        const url = `${this.prefix}/GetActiveOpportunityAssociatedEpcCalculations/${partnerSystemNumber}`;
        return this.igsApiClientService.get(url);
    }

    public getCustomerContractSignDate = (customerDetailsRequest: Models.Customer.SolarAccountDetailsRequest.SolarAccountDetailsRequest): ng.IPromise<Date> => {
        const url = `${this.prefix}/GetCustomerContractSignDate`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.post(url, customerDetailsRequest, config);
    }

    public removeOpportunityAssociatedEpcCalculation = (removeEpcCalculationRequest: Models.CostCalculation.RemoveEpcCalculationRequest.IRemoveEpcCalculationRequest): ng.IPromise<void> => {
        const url = `${this.prefix}/RemoveOpportunityAssociatedEpcCalculation`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.post(url, removeEpcCalculationRequest, config);
    }

    public removeOpportunityAssociatedEpcCalculationAndVoidEnvelopes = (removeEpcRequest: Models.CostCalculation.RemoveEpcCalculationRequest.IRemoveEpcCalculationRequest): ng.IPromise<void> => {
        const url = `${this.prefix}/RemoveOpportunityAssociatedEpcCalculationAndVoidEnvelopes`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.post(url, removeEpcRequest, config);
    }

    public getContractEpcCalculation = (igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult> => {
        const url = `${this.prefix}/GetContractEpcCalculation/${igsCustomerNumber}`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.get(url, config);
    }

    public getPendingEpcCalculation = (igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult> => {
        const url = `${this.prefix}/GetPendingEpcCalculation/${igsCustomerNumber}`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.get(url, config);
    }

    public updateUnshadedPercent = (request: Models.CostCalculation.UpdateUnshadedPercentRequest.UpdateUnshadedPercentRequest): ng.IPromise<void> => {
        const url = `${this.prefix}/UpdateUnshadedPercent`;
        return this.igsApiClientService.post(url, request);
    }


    public isContractAmendmentRateInProgress = (igsCustomerNumber: string): ng.IPromise<boolean> => {
        const url = `${this.prefix}/IsContractAmendmentRateInProgress/${igsCustomerNumber}`;
        const config = { errorHandler: () => { } };
        return this.igsApiClientService.get(url, config);
    }

    public cancelContractAmendment = (igsCustomerNumber: string): angular.IPromise<void> => {
        const url = `${this.prefix}/CancelContractAmendment/${igsCustomerNumber}`;
        return this.igsApiClientService.post(url, null);
    }

    public getProductionEstimateReconcileSummary = (igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.ProductionEstimateResult.ProductionEstimateReconcileSummary> => {
        const url = `${this.prefix}/ProductionEstimateReconcileSummary/${igsCustomerNumber}`;
        return this.igsApiClientService.get(url);
    }

    public calculateEpcAndReconcileWithProductionEstimate = (igsCustomerNumber: string): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult> => {
        const url = `${this.prefix}/CalculateEpcAndReconcileWithProductionEstimate/${igsCustomerNumber}`;
        return this.igsApiClientService.post(url, null);
    }

    public confirmReconciledSystemData = (igsCustomerNumber: string): ng.IPromise<void> => {
        const url = `${this.prefix}/ConfirmReconciledSystemData/${igsCustomerNumber}`;
        return this.igsApiClientService.post(url, null);
    }

    public reconcileEpcCostWithOverride = (overrideRequest: IEpcOverrideReconcileRequest): ng.IPromise<Models.CostCalculation.EpcCalculation.IContractAssociatedEpcCalculationResult> => {
      const url = `${this.prefix}/ReconcileEpcCostWithOverride`;
      return this.igsApiClientService.post(url, overrideRequest);
    }
}
